<ng-container *transloco="let t; prefix: 'maintenanceOverview.modals.edit'">
  <div class="modal-header">
    <h5 class="modal-title">{{ t('title') }}</h5>
    <button type="button" class="btn-close" [attr.aria-label]="'common.close' | transloco" (click)="closeModal()">
      <i class="ph ph-x"></i>
    </button>
  </div>

  <div class="modal-body">
    <form [formGroup]="editForm" (ngSubmit)="onSubmit()">
      <!-- Asset Name (disabled) -->
      <div class="form-group">
        <label>{{ t('assetName') }}</label>
        <input type="text" class="form-control" formControlName="assetName" readonly />
      </div>

      <!-- Make + Model (disabled) -->
      <div class="form-row">
        <div class="form-group half-width">
          <label>{{ t('make') }}</label>
          <input type="text" class="form-control" formControlName="make" readonly />
        </div>
        <div class="form-group half-width">
          <label>{{ t('model') }}</label>
          <input type="text" class="form-control" formControlName="model" readonly />
        </div>
      </div>

      <!-- Source (disabled) -->
      <div class="form-group">
        <label>{{ t('source') }}</label>
        <input type="text" class="form-control" formControlName="source" readonly />
      </div>

      <!-- Interval + Distance Over (disabled) -->
      <div class="form-row">
        <div class="form-group half-width">
          <label>{{ t('interval') }}</label>
          <input type="text" class="form-control" formControlName="interval" readonly />
        </div>
        <div class="form-group half-width">
          <label>{{ t('distanceOver') }}</label>
          <input type="text" class="form-control" formControlName="distanceOver" readonly />
        </div>
      </div>

      <!-- Service Type + Maintenance Reason -->
      <div class="form-row">
        <div class="form-group half-width">
          <label>{{ t('serviceType') }} <span class="required">*</span></label>

          <div ngbDropdown class="custom-dropdown" [autoClose]="true">
            <button type="button" class="btn form-control dropdown-toggle" ngbDropdownToggle>
              <div class="d-flex flex-row justify-content-between dropdown-inner">
                <span class="align-middle">{{ serviceTypeLabel }}</span>
                <i class="ph ph-caret-down lh-base"></i>
              </div>
            </button>

            <div ngbDropdownMenu>
              <div class="d-flex flex-column">
                @for (option of serviceTypeOptions; track option.value) {
                  <button type="button" class="btn btn-outline" ngbDropdownItem (click)="onServiceTypeSelect(option.value, $event)">
                    {{ option.label }}
                  </button>
                }
              </div>
            </div>
          </div>

          @if (editForm.get('serviceType')?.invalid && editForm.get('serviceType')?.touched) {
            <div class="error-message">{{ t('serviceTypeRequired') }}</div>
          }
        </div>

        <div class="form-group half-width">
          <label>{{ t('maintenanceReason') }} <span class="required">*</span></label>

          <div ngbDropdown class="custom-dropdown reason-dropdown" [autoClose]="true">
            <button type="button" class="btn form-control dropdown-toggle" ngbDropdownToggle>
              <div class="d-flex flex-row justify-content-between dropdown-inner">
                <span class="align-middle">{{ maintenanceReasonLabel }}</span>
                <i class="ph ph-caret-down lh-base"></i>
              </div>
            </button>

            <div ngbDropdownMenu>
              <div class="d-flex flex-column">
                @if (isLoadingReasons) {
                  <div class="dropdown-loading">
                    <span class="spinner-border spinner-border-sm"></span>
                    <span>{{ t('loadingReasons') }}</span>
                  </div>
                } @else if (maintenanceReasons.length === 0) {
                  <div class="dropdown-loading">
                    <span>{{ t('noReasonsFound') }}</span>
                  </div>
                } @else {
                  @for (r of maintenanceReasons; track r.id) {
                    <button type="button" class="btn btn-outline" ngbDropdownItem (click)="onMaintenanceReasonSelect(r, $event)">
                      {{ r.localizedName || r.name }}
                    </button>
                  }
                }
              </div>
            </div>
          </div>

          @if (editForm.get('maintenanceReason')?.invalid && editForm.get('maintenanceReason')?.touched) {
            <div class="error-message">{{ t('maintenanceReasonRequired') }}</div>
          }
        </div>
      </div>

      <!-- Workshop -->
      <div class="form-group">
        <label>{{ t('workshop') }} <span class="required">*</span></label>

        <div ngbDropdown class="custom-dropdown" [autoClose]="true">
          <button type="button" class="btn form-control dropdown-toggle" ngbDropdownToggle>
            <div class="d-flex flex-row justify-content-between dropdown-inner">
              <span class="align-middle">{{ workshopLabel }}</span>
              <i class="ph ph-caret-down lh-base"></i>
            </div>
          </button>

          <div ngbDropdownMenu>
            <div class="d-flex flex-column">
              @if (isLoadingWorkshops) {
                <div class="dropdown-loading">
                  <span class="spinner-border spinner-border-sm"></span>
                  <span>{{ t('loadingWorkshops') }}</span>
                </div>
              } @else if (workshops.length === 0) {
                <div class="dropdown-loading">
                  <span>{{ t('noWorkshopsFound') }}</span>
                </div>
              } @else {
                @for (w of workshops; track w.id) {
                  <button type="button" class="btn btn-outline" ngbDropdownItem (click)="onWorkshopSelect(w, $event)">
                    {{ w.name }}
                  </button>
                }
              }
            </div>
          </div>
        </div>

        @if (editForm.get('workshop')?.invalid && editForm.get('workshop')?.touched) {
          <div class="error-message">{{ t('workshopRequired') }}</div>
        }
      </div>

      <!-- Scheduled Date + Priority Level -->
      <div class="form-row">
        <div class="form-group half-width">
          <app-flatpickr-date-picker
            [label]="t('dueDate')"
            [(value)]="dueDate"
            (valueChange)="onDueDateChange($event ?? '')"
            [minDate]="'today'"
            [altFormat]="'d/m/Y'"
            [placeholder]="'dd/mm/yyyy'"
            [required]="true">
          </app-flatpickr-date-picker>

          @if (editForm.get('dueDate')?.invalid && editForm.get('dueDate')?.touched) {
            <div class="error-message">{{ t('scheduledDateRequired') }}</div>
          }
        </div>

        <div class="form-group half-width">
          <label>{{ t('priorityLevel') }}</label>

          <div ngbDropdown class="custom-dropdown" [autoClose]="true">
            <button type="button" class="btn form-control dropdown-toggle" ngbDropdownToggle>
              <div class="d-flex flex-row justify-content-between dropdown-inner">
                <span class="align-middle">{{ priorityLevelLabel }}</span>
                <i class="ph ph-caret-down lh-base"></i>
              </div>
            </button>

            <div ngbDropdownMenu>
              <div class="d-flex flex-column">
                @for (p of priorityLevelOptions; track p.value) {
                  <button type="button" class="btn btn-outline" ngbDropdownItem (click)="onPriorityLevelSelect(p.value, $event)">
                    {{ p.label }}
                  </button>
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button type="button" class="cancel-btn" (click)="onCancel()" [disabled]="isLoading">{{ 'common.cancel' | transloco }}</button>

        <button type="submit" class="add-btn" [disabled]="isLoading || editForm.invalid || !hasChanges">
          @if (isLoading) {
            <span class="spinner-border"></span>
          }
          @if (!isLoading) {
            <span>{{ t('save') }}</span>
          }
        </button>
      </div>
    </form>
  </div>
</ng-container>